import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { locale } from "@/lib/format";
import { useSearchParams } from "react-router-dom";
import { useApiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { Plus, ChevronLeft, ChevronRight, Receipt, Calendar, BarChart3 } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";

import {
  ymd, isoAt, startOfWeek, addDays, startOfMonth,
  slotInRange,
  DEFAULT_OPEN_HOURS, MONTH_LABELS,
} from "./agenda/utils";
import { MobileDayList } from "./agenda/MobileDayList";
import { DatePickerLabel } from "./agenda/DatePickerLabel";
import { TimelineDay, TimelineStaff, TimelineWeek } from "./agenda/Timeline";
import { MonthView } from "./agenda/MonthView";
import { ApptModal } from "./agenda/ApptModal";
import { CompleteModal } from "./agenda/CompleteModal";
import { EndOfDayModal } from "./agenda/EndOfDayModal";
import { DayOverviewModal } from "./agenda/DayOverviewModal";

export default function Agenda() {
  const { t } = useTranslation();
  const api = useApiClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState("day");
  const [dayMode, setDayMode] = useState("time"); // time | staff
  const [colorBy] = useState("staff"); // staff | service (only for day)
  const [date, setDate] = useState(() => searchParams.get("date") || ymd(new Date()));
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [products, setProducts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm(date));
  const [activeDrag, setActiveDrag] = useState(null);
  const [completingAppt, setCompletingAppt] = useState(null); // appointment object
  const [showEodModal, setShowEodModal] = useState(false);   // "Chiusura giornata" wizard
  const [showOverview, setShowOverview] = useState(false);   // Panoramica del giorno

  function emptyForm(d) {
    return { client_id: "", service_items: [], staff_id: "", start_date: d, start_time: "10:00", end_time: "10:30", notes: "", status: "scheduled" };
  }

  const load = async () => {
    let from, to;
    if (view === "day") { from = `${date}T00:00:00`; to = `${date}T23:59:59`; }
    else if (view === "week") { const s = startOfWeek(date); from = `${s}T00:00:00`; to = `${addDays(s, 6)}T23:59:59`; }
    else { const s = startOfMonth(date); from = `${s}T00:00:00`; to = `${addDays(s, 35)}T23:59:59`; }
    const [a, c, sv, st, t, pr, pk, ov] = await Promise.all([
      api.get(`/appointments?date_from=${from}&date_to=${to}`),
      api.get("/clients"), api.get("/services"), api.get("/staff"), api.get("/tenant").catch(() => ({ data: null })),
      api.get("/products").catch(() => ({ data: [] })),
      api.get("/packages").catch(() => ({ data: [] })),
      api.get(`/hours-overrides?from=${from.slice(0, 10)}&to=${to.slice(0, 10)}`).catch(() => ({ data: [] })),
    ]);
    setItems(a.data); setClients(c.data); setServices(sv.data); setStaff(st.data); setTenant(t.data); setProducts(pr.data); setPackages(pk.data); setOverrides(ov.data);
  };
  useEffect(() => { load(); }, [date, view]); // eslint-disable-line

  // If URL has ?edit={id}, auto-open the modal. If the appointment is on
  // another date, jump to that date first then reopen once items reload.
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId) return;
    const appt = items.find((x) => x.id === editId);
    if (appt) {
      openEdit(appt);
      searchParams.delete("edit");
      setSearchParams(searchParams, { replace: true });
      return;
    }
    // Not in current date's items → fetch specific appt and jump to its date
    if (items.length === 0) return; // wait first load
    let cancelled = false;
    api.get(`/appointments/${editId}`).then((r) => {
      if (cancelled || !r.data) return;
      const apptDate = ymd(new Date(r.data.start));
      if (apptDate !== date) {
        // change date → items reload → this effect re-runs and will find it
        setDate(apptDate);
      } else {
        openEdit(r.data);
        searchParams.delete("edit");
        setSearchParams(searchParams, { replace: true });
      }
    }).catch(() => {
      toast.error(t("agenda.notFound"));
      searchParams.delete("edit");
      setSearchParams(searchParams, { replace: true });
    });
    return () => { cancelled = true; };
  }, [items, searchParams, setSearchParams, date]); // eslint-disable-line

  // Se URL ?new=1[&client_id=X] apri il modal "Nuovo appuntamento" prefillato
  useEffect(() => {
    if (searchParams.get("new") !== "1") return;
    const cid = searchParams.get("client_id") || "";
    setEditing(null);
    setForm({ ...emptyForm(date), client_id: cid });
    setShowForm(true);
    searchParams.delete("new");
    searchParams.delete("client_id");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, date]); // eslint-disable-line

  const openHours = tenant?.opening_hours || DEFAULT_OPEN_HOURS;

  // Return the effective day config for a date, respecting hours_overrides:
  // - kind='closed' override → {closed:true}
  // - kind='open' override   → uses its own open/close/open2/close2
  // - otherwise → weekly openHours for that DOW
  const dayCfgFor = (dateStr, staffId = null) => {
    // Priority: 1) staff-specific override for date, 2) salon-wide override,
    // 3) staff working_hours intersected with salon weekly, 4) salon weekly.
    const staffOv = staffId ? overrides.find((o) => o.date === dateStr && o.staff_id === staffId) : null;
    const salonOv = overrides.find((o) => o.date === dateStr && !o.staff_id);
    if (staffOv) {
      if (staffOv.kind === "closed") return { closed: true, _override: true, _scope: "staff" };
      return { closed: false, open: staffOv.open, close: staffOv.close, open2: staffOv.open2, close2: staffOv.close2, _override: true, _scope: "staff" };
    }
    if (salonOv) {
      if (salonOv.kind === "closed") return { closed: true, _override: true, _scope: "salon" };
      return { closed: false, open: salonOv.open, close: salonOv.close, open2: salonOv.open2, close2: salonOv.close2, _override: true, _scope: "salon" };
    }
    const dow = new Date(dateStr + "T00:00:00Z").getUTCDay();
    const key = String(dow === 0 ? 6 : dow - 1);
    return openHours[key] || { closed: true };
  };

  const clientOf = (id) => clients.find((c) => c.id === id);
  const staffOf = (id) => staff.find((s) => s.id === id);
  const servicesOf = (appt) => {
    const ids = (appt.service_ids && appt.service_ids.length ? appt.service_ids : (appt.service_id ? [appt.service_id] : []));
    return ids.map((i) => services.find((s) => s.id === i)).filter(Boolean);
  };
  const chipColor = (appt) => {
    if (view === "day" && colorBy === "service") {
      return servicesOf(appt)[0]?.color || "#06B6D4";
    }
    return staffOf(appt.staff_id)?.color || "#06B6D4";
  };
  const chipTitle = (appt) => {
    const svcs = servicesOf(appt);
    return {
      primary: clientOf(appt.client_id)?.name || "Cliente",
      secondary: svcs.map((s) => s.name).join(" · ") || "—",
    };
  };
  const apptMinutes = (appt) => Math.max(15, (new Date(appt.end) - new Date(appt.start)) / 60000);

  // Compute timeline segments for an appt: alternating service+buffer chunks
  // routed per operator. Returns [{ kind:'service'|'buffer', staff_id, start,
  // end, service_id?, service_name?, service_color?, buffer_min?, appt,
  // seg_index, is_first_service }]. When no service_items are present we
  // fallback to a single service segment covering the full appt duration.
  const segmentsFor = (appt) => {
    const items = appt.service_items?.length
      ? appt.service_items
      : (appt.service_ids || []).map((sid) => ({ service_id: sid, staff_id: null, buffer_after_min: 0 }));
    const primary = appt.staff_id;
    const startMs = new Date(appt.start).getTime();
    let cursor = startMs;
    const segs = [];
    let firstService = true;
    items.forEach((it, i) => {
      const svc = services.find((s) => s.id === it.service_id);
      const dur = svc?.duration_min || 0;
      const staffId = it.staff_id || primary;
      if (dur > 0) {
        const s = new Date(cursor), e = new Date(cursor + dur * 60000);
        segs.push({ kind: "service", staff_id: staffId, start: s, end: e,
                    service_id: it.service_id, service_name: svc?.name,
                    service_color: svc?.color, appt, seg_index: i, is_first_service: firstService });
        firstService = false;
        cursor = e.getTime();
      }
      const buf = Number(it.buffer_after_min || 0);
      if (buf > 0) {
        const s = new Date(cursor), e = new Date(cursor + buf * 60000);
        segs.push({ kind: "buffer", staff_id: staffId, start: s, end: e,
                    buffer_min: buf, appt, seg_index: i });
        cursor = e.getTime();
      }
    });
    if (!segs.length) {
      segs.push({ kind: "service", staff_id: primary, start: new Date(appt.start), end: new Date(appt.end), appt, seg_index: 0, is_first_service: true });
    }
    return segs;
  };

  const weekDays = useMemo(() => {
    const s = startOfWeek(date);
    return Array.from({ length: 7 }, (_, i) => addDays(s, i));
  }, [date]);
  const monthGrid = useMemo(() => {
    const som = startOfMonth(date);
    const first = new Date(som + "T00:00:00Z").getUTCDay();
    const off = first === 0 ? -6 : 1 - first;
    const start = addDays(som, off);
    return Array.from({ length: 42 }, (_, i) => addDays(start, i));
  }, [date]);
  const byDay = useMemo(() => {
    const m = {};
    for (const a of items) { const k = ymd(new Date(a.start)); (m[k] = m[k] || []).push(a); }
    return m;
  }, [items]);

  const shift = (delta) => {
    if (view === "day") { const d = new Date(date); d.setDate(d.getDate() + delta); setDate(ymd(d)); }
    else if (view === "week") setDate(addDays(date, delta * 7));
    else { const d = new Date(date + "T00:00:00Z"); d.setUTCMonth(d.getUTCMonth() + delta); setDate(ymd(d)); }
  };

  const openForm = (prefill = {}) => { setEditing(null); setForm({ ...emptyForm(date), ...prefill }); setShowForm(true); };
  const openEdit = (appt) => {
    const start = new Date(appt.start), end = new Date(appt.end);
    setEditing(appt.id);
    // Prefer new service_items shape; fall back to legacy service_ids/service_id.
    let service_items = appt.service_items || [];
    if (!service_items.length) {
      const ids = appt.service_ids?.length ? appt.service_ids : (appt.service_id ? [appt.service_id] : []);
      service_items = ids.map((sid) => ({ service_id: sid, staff_id: null, buffer_after_min: 0 }));
    }
    setForm({
      client_id: appt.client_id,
      service_items,
      staff_id: appt.staff_id,
      start_date: ymd(start),
      start_time: `${String(start.getUTCHours()).padStart(2, "0")}:${String(start.getUTCMinutes()).padStart(2, "0")}`,
      end_time: `${String(end.getUTCHours()).padStart(2, "0")}:${String(end.getUTCMinutes()).padStart(2, "0")}`,
      notes: appt.notes || "",
      status: appt.status || "scheduled",
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service_items.length) { toast.error(t("agenda.addService")); return; }
    const [sh, sm] = form.start_time.split(":").map(Number);
    const [eh, em] = form.end_time.split(":").map(Number);
    const start = isoAt(form.start_date, sh, sm);
    const end = isoAt(form.start_date, eh, em);
    if (new Date(end) <= new Date(start)) { toast.error(t("agenda.endBeforeStart")); return; }
    // If outside opening hours or staff working hours, ask confirmation
    const st = staffOf(form.staff_id);
    const inHoursSalon = slotInRange(form.start_date, sh, sm, openHours);
    const inHoursStaff = !st?.working_hours || slotInRange(form.start_date, sh, sm, st.working_hours);
    if (!inHoursSalon || !inHoursStaff) {
      const msg = !inHoursSalon
        ? "Questo appuntamento è fuori dagli orari di apertura del salone. Confermi comunque?"
        : "Il collaboratore selezionato non risulta in servizio in questo orario. Confermi comunque?";
      if (!window.confirm(msg)) return;
    }
    // Derive legacy service_ids for backward-compat + primary staff.
    const service_ids = form.service_items.map((it) => it.service_id);
    const primary_staff = form.staff_id
      || form.service_items.find((it) => it.staff_id)?.staff_id
      || "";
    const payload = {
      client_id: form.client_id,
      service_ids,
      service_items: form.service_items.map((it) => ({
        service_id: it.service_id,
        staff_id: it.staff_id || null,
        buffer_after_min: Number(it.buffer_after_min || 0),
      })),
      staff_id: primary_staff,
      start, end,
      notes: form.notes || null, status: form.status,
    };
    try {
      if (editing) await api.patch(`/appointments/${editing}`, payload);
      else await api.post("/appointments", payload);
      toast.success(editing ? "Aggiornato" : "Creato");
      setShowForm(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };
  const remove = async (id) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/appointments/${id}`); toast.success(t("agenda.removed")); load(); };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const isAvailable = (targetDate, hour, minute, staffId) => {
    // Prefer the staff-specific effective config when a staff is provided.
    const cfg = dayCfgFor(targetDate, staffId);
    if (cfg.closed) return false;
    const t = hour * 60 + minute;
    const timeToMin = (hhmm) => { const [h, m] = (hhmm || "").split(":").map(Number); return h * 60 + m; };
    const morning = cfg.open && cfg.close && timeToMin(cfg.open) <= t && t < timeToMin(cfg.close);
    const afternoon = cfg.open2 && cfg.close2 && timeToMin(cfg.open2) <= t && t < timeToMin(cfg.close2);
    if (!morning && !afternoon) return false;
    // If no staff-specific override was in play, still respect the staff's
    // weekly working_hours so ferie ricorrenti (turno) restano rispettate.
    if (cfg._scope !== "staff") {
      const st = staffOf(staffId);
      if (st?.working_hours && !slotInRange(targetDate, hour, minute, st.working_hours)) return false;
    }
    return true;
  };
  const onDragEnd = async (event) => {
    setActiveDrag(null);
    const { active, over } = event;
    if (!over) return;
    const [appId] = String(active.id).split("::");
    const parts = String(over.id).split("::");
    const targetDate = parts[0];
    const [th, tm] = parts[1].split(":").map(Number);
    const targetStaffId = parts[2] || null;
    const appt = items.find((x) => x.id === appId);
    if (!appt) return;
    const dur = apptMinutes(appt);
    const staffForCheck = targetStaffId || appt.staff_id;
    if (!isAvailable(targetDate, th, tm, staffForCheck)) {
      if (!window.confirm("Questo slot è fuori dagli orari lavorativi. Confermi lo spostamento?")) return;
    }
    const newStart = isoAt(targetDate, th, tm);
    const newEnd = new Date(new Date(newStart).getTime() + dur * 60000).toISOString();
    setItems((prev) => prev.map((x) => x.id === appId ? { ...x, start: newStart, end: newEnd, staff_id: targetStaffId || x.staff_id } : x));
    try {
      await api.patch(`/appointments/${appId}`, {
        client_id: appt.client_id,
        service_ids: appt.service_ids?.length ? appt.service_ids : (appt.service_id ? [appt.service_id] : []),
        // Preserve per-line staff and buffer minutes when dragging.
        service_items: (appt.service_items || []).map((it) => ({
          service_id: it.service_id,
          staff_id: it.staff_id || null,
          buffer_after_min: Number(it.buffer_after_min || 0),
        })),
        staff_id: targetStaffId || appt.staff_id, start: newStart, end: newEnd,
        notes: appt.notes || null, status: appt.status || "scheduled",
      });
      toast.success(t("agenda.moved"));
    } catch { toast.error(t("agenda.revertError")); load(); }
  };
  const activeAppt = activeDrag ? items.find((x) => x.id === activeDrag.split("::")[0]) : null;

  const headerLabel = view === "month"
    ? `${MONTH_LABELS[new Date(date + "T00:00:00Z").getUTCMonth()]} ${new Date(date + "T00:00:00Z").getUTCFullYear()}`
    : (view === "week"
      ? `${startOfWeek(date).slice(8, 10)}/${startOfWeek(date).slice(5, 7)} – ${addDays(startOfWeek(date), 6).slice(8, 10)}/${addDays(startOfWeek(date), 6).slice(5, 7)} · ${new Date(date + "T00:00:00Z").getUTCFullYear()}`
      : new Date(date + "T00:00:00Z").toLocaleDateString(locale(), { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }));

  return (
    <div data-testid="agenda-page" className="space-y-4">
      {/* Row 1: title + azioni primarie */}
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-display text-2xl sm:text-4xl font-black tracking-tight inline-flex items-center gap-2 min-w-0">
          <Calendar className="h-6 w-6 sm:h-7 sm:w-7 text-[color:var(--sp-brand)] flex-shrink-0" />
          Agenda
        </h1>
        <div className="inline-flex items-center gap-2 flex-shrink-0">
          <button data-testid="agenda-overview-btn" onClick={() => setShowOverview(true)}
                  className="sp-btn-ghost inline-flex items-center gap-1.5 text-xs sm:text-sm" title="Panoramica del giorno">
            <BarChart3 className="h-4 w-4" /> <span className="hidden sm:inline">{t("agenda.overview")}</span>
          </button>
          <button data-testid="agenda-eod-btn" onClick={() => setShowEodModal(true)}
                  className="sp-btn-ghost inline-flex items-center gap-1.5 text-xs sm:text-sm" title="Chiudi giornata">
            <Receipt className="h-4 w-4" /> <span className="hidden sm:inline">{t("agenda.closed")}</span>
          </button>
          <button data-testid="agenda-new-btn" onClick={() => openForm()} className="sp-btn-primary inline-flex items-center gap-1.5 text-xs sm:text-sm"><Plus className="h-4 w-4" /> Nuovo</button>
        </div>
      </header>

      {/* Row 2: navigazione data + toggle vista/modalità (barra separata "toolbar") */}
      <div className="sp-card p-2.5 flex items-center flex-wrap gap-2" data-testid="agenda-toolbar">
        <div className="inline-flex items-center gap-1 flex-shrink-0">
          <button data-testid="agenda-prev" onClick={() => shift(-1)} className="sp-btn-ghost !p-1.5" title="Precedente">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <DatePickerLabel value={date} onChange={setDate} label={headerLabel} />
          <button data-testid="agenda-next" onClick={() => shift(1)} className="sp-btn-ghost !p-1.5" title="Successivo">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Gruppo toggles a destra: Ore/Staff + Giorno/Settimana/Mese sempre uniti sulla stessa riga */}
        <div className="inline-flex items-center gap-2 flex-wrap justify-end ml-auto">
          {view === "day" && (
            <div className="inline-flex rounded-full border border-[color:var(--sp-border)] bg-white p-1">
              <button data-testid="agenda-daymode-time" onClick={() => setDayMode("time")}
                className={`px-3 py-1 rounded-full text-xs font-bold ${dayMode === "time" ? "bg-[color:var(--sp-accent)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}>{t("agenda.hours")}</button>
              <button data-testid="agenda-daymode-staff" onClick={() => setDayMode("staff")}
                className={`px-3 py-1 rounded-full text-xs font-bold ${dayMode === "staff" ? "bg-[color:var(--sp-accent)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}>{t("nav.staff")}</button>
            </div>
          )}
          <div className="inline-flex rounded-full border border-[color:var(--sp-border)] bg-white p-1">
            {[["day", "Giorno"], ["week", "Settimana"], ["month", "Mese"]].map(([k, l]) => (
              <button key={k} data-testid={`agenda-view-${k}`} onClick={() => setView(k)}
                className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${view === k ? "bg-[color:var(--sp-brand)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      <DndContext sensors={sensors} onDragStart={(e) => setActiveDrag(e.active.id)} onDragEnd={onDragEnd} onDragCancel={() => setActiveDrag(null)}>
        {view === "day" && dayMode === "time" && (
          <>
            {/* Desktop: timeline verticale griglia ore */}
            <div className="hidden sm:block">
              <TimelineDay date={date} items={items} openHours={openHours} chipColor={chipColor} chipTitle={chipTitle} onEdit={openEdit} onDelete={remove} openForm={openForm} apptMinutes={apptMinutes} segmentsFor={segmentsFor} />
            </div>
            {/* Mobile: agenda verticale scrollabile stile app */}
            <div className="sm:hidden">
              <MobileDayList date={date} items={items} clientOf={clientOf} staffOf={staffOf} servicesOf={servicesOf} chipColor={chipColor} onEdit={openEdit} openForm={openForm} openHours={openHours} />
            </div>
          </>
        )}

        {view === "day" && dayMode === "staff" && (
          <>
            <div className="hidden sm:block">
              <TimelineStaff date={date} staff={staff} items={items} openHours={openHours} chipColor={chipColor} chipTitle={chipTitle} onEdit={openEdit} onDelete={remove} openForm={openForm} apptMinutes={apptMinutes} segmentsFor={segmentsFor} dayCfgForColumn={dayCfgFor} />
            </div>
            {/* Su mobile la vista "per staff" affiancata non ha senso: torna alla lista verticale raggruppata per staff */}
            <div className="sm:hidden">
              <MobileDayList date={date} items={items} clientOf={clientOf} staffOf={staffOf} servicesOf={servicesOf} chipColor={chipColor} onEdit={openEdit} openForm={openForm} openHours={openHours} groupByStaff staff={staff} />
            </div>
          </>
        )}

        {view === "week" && (
          <>
            {/* Desktop: griglia settimanale */}
            <div className="hidden sm:block">
              <TimelineWeek weekDays={weekDays} items={items} openHours={openHours} chipColor={chipColor} chipTitle={chipTitle} onEdit={openEdit} openForm={openForm} apptMinutes={apptMinutes} segmentsFor={segmentsFor} today={ymd(new Date())} />
            </div>
            {/* Mobile: banner + card giorno per giorno (niente griglia orizzontale forzata) */}
            <div className="sm:hidden space-y-3">
              <div className="sp-card p-3 text-xs text-[color:var(--sp-text-secondary)] flex items-center gap-2">
                <Calendar className="h-4 w-4 text-[color:var(--sp-brand)] flex-shrink-0" />
                <span>Vista settimana compatta — tocca un giorno per aprire l'agenda dettagliata.</span>
              </div>
              {weekDays.map((d) => {
                const dayItems = items.filter((a) => (a.start || "").slice(0, 10) === d);
                const label = new Date(d + "T00:00:00Z").toLocaleDateString(locale(), { weekday: "long", day: "2-digit", month: "short", timeZone: "UTC" });
                return (
                  <button key={d} onClick={() => { setDate(d); setView("day"); }} data-testid={`mobile-week-day-${d}`}
                          className="sp-card p-3 w-full text-left hover:shadow-lg transition-shadow">
                    <div className="flex items-baseline justify-between mb-2">
                      <div className="font-display font-black capitalize text-sm">{label}</div>
                      <div className="text-[10px] label-eyebrow">{dayItems.length} appt.</div>
                    </div>
                    {dayItems.length === 0 ? (
                      <div className="text-[11px] text-[color:var(--sp-text-muted)]">{t("agenda.noAppointments")}</div>
                    ) : (
                      <div className="space-y-1">
                        {dayItems.slice(0, 3).map((a) => (
                          <div key={a.id} className="flex items-center gap-2 text-xs">
                            <span className="w-1.5 h-4 rounded-sm flex-shrink-0" style={{ background: chipColor(a) }} />
                            <span className="font-bold tabular-nums text-[color:var(--sp-text-secondary)]">{a.start?.slice(11, 16)}</span>
                            <span className="truncate">{clientOf(a.client_id)?.name || "Cliente"}</span>
                          </div>
                        ))}
                        {dayItems.length > 3 && (
                          <div className="text-[10px] text-[color:var(--sp-text-muted)]">+ altri {dayItems.length - 3}</div>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {view === "month" && (
          <MonthView currentMonth={new Date(date + "T00:00:00Z").getUTCMonth()} monthGrid={monthGrid} byDay={byDay} onPickDay={(d) => { setDate(d); setView("day"); }} chipColor={chipColor} chipTitle={chipTitle} today={ymd(new Date())} />
        )}

        <DragOverlay dropAnimation={null}>
          {activeAppt && (
            <div className="appt-chip-week" style={{ background: chipColor(activeAppt), minWidth: 140 }}>
              {chipTitle(activeAppt).primary}
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {showForm && (
        <ApptModal onClose={() => setShowForm(false)} onSubmit={submit} onDelete={editing ? () => { remove(editing); setShowForm(false); } : null}
                   onComplete={editing ? () => {
                     const a = items.find((x) => x.id === editing);
                     if (a) { setShowForm(false); setCompletingAppt(a); }
                   } : null}
                   form={form} setForm={setForm} clients={clients} services={services} staff={staff} editing={editing} openHours={openHours} dayCfgFor={dayCfgFor} isAvailable={isAvailable} />
      )}

      {completingAppt && (
        <CompleteModal
          appt={completingAppt}
          clients={clients}
          services={services}
          products={products}
          packages={packages}
          onClose={() => setCompletingAppt(null)}
          onSaved={() => { setCompletingAppt(null); load(); }}
        />
      )}

      {showOverview && (
        <DayOverviewModal date={date} onClose={() => setShowOverview(false)} />
      )}

      {showEodModal && (
        <EndOfDayModal
          date={date}
          items={items}
          clients={clients}
          onClose={() => setShowEodModal(false)}
          onPickAppt={(a) => setCompletingAppt(a)}
        />
      )}
    </div>
  );
}
